import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api } from '@/api';
import type { IWorkbookData } from '@univerjs/core';
import type {
  Artifact,
  ArtifactDetail,
  ArtifactFilter,
  ArtifactSharing,
} from '@/features/artifacts/types';
import { artifactRevisions } from '@/features/artifacts/api';

export const ARTIFACTS_QUERY_KEY = 'artifacts';

export const artifactsQuery = (filter: ArtifactFilter & { limit?: number }) =>
  infiniteQueryOptions({
    queryKey: [ARTIFACTS_QUERY_KEY, 'list', filter],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<{ items: Artifact[]; nextOffset: number | null; totalCount: number }>(
          'v1/artifacts',
          { params: { ...filter, offset: pageParam } },
        )
      ).data,
    initialPageParam: 0,
    getNextPageParam: (page) => page.nextOffset,
    placeholderData: keepPreviousData,
    refetchInterval: 15000,
  });

export const artifactsCountQuery = (
  scope: Pick<ArtifactFilter, 'organizationId' | 'projectId' | 'workspaceId'>,
) =>
  infiniteQueryOptions({
    ...artifactsQuery({ ...scope, limit: 1 }),
    select: (data) => data.pages[0]?.totalCount ?? 0,
    placeholderData: undefined,
    refetchInterval: false,
  });

export const artifactQuery = (artifactId: string, revision?: number) =>
  queryOptions({
    queryKey: [ARTIFACTS_QUERY_KEY, artifactId, revision],
    queryFn: async () =>
      (await api.get<ArtifactDetail>(`v1/artifacts/${artifactId}`, { params: { revision } })).data,
  });

export const artifactSharingQuery = (artifactId: string) =>
  queryOptions({
    queryKey: [ARTIFACTS_QUERY_KEY, artifactId, 'sharing'],
    queryFn: async () =>
      (await api.get<ArtifactSharing>(`v1/artifacts/${artifactId}/sharing`)).data,
  });

const REVISIONS_PAGE_SIZE = 50;

export const artifactRecentRevisionsQuery = (artifactId: string) =>
  queryOptions({
    queryKey: [ARTIFACTS_QUERY_KEY, artifactId, 'revisions', 'recent'],
    queryFn: async () =>
      (await artifactRevisions(artifactId, { offset: 0, limit: REVISIONS_PAGE_SIZE })).data,
  });

export const artifactRevisionsQuery = (artifactId: string) =>
  infiniteQueryOptions({
    queryKey: [ARTIFACTS_QUERY_KEY, artifactId, 'revisions'],
    queryFn: async ({ pageParam }) =>
      (await artifactRevisions(artifactId, { offset: pageParam, limit: REVISIONS_PAGE_SIZE })).data,
    initialPageParam: 0,
    getNextPageParam: (page, pages) =>
      page.length ? pages.length * REVISIONS_PAGE_SIZE : undefined,
  });

export const spreadsheetWorkbookQuery = (url: string) =>
  queryOptions({
    queryKey: ['spreadsheetWorkbook', url],
    queryFn: async ({ signal }) => {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Workbook could not be loaded');
      return (
        await api.post<IWorkbookData>('v1/artifacts/spreadsheets/convert', await response.blob(), {
          headers: { 'Content-Type': 'application/octet-stream' },
          signal,
        })
      ).data;
    },
    staleTime: Infinity,
  });
